// src/hooks/useAudioToast.js
import { useCallback } from 'react';
import { toast } from 'react-toastify';
import audioNotificationService from '../services/audioNotificationService';

const useAudioToast = () => {
  // Enhanced toast functions with audio
  const showToast = useCallback(async (message, type = 'info', options = {}) => {
    // Show the toast notification
    const toastOptions = {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options
    };

    let toastId;
    switch (type) {
      case 'success':
        toastId = toast.success(message, toastOptions);
        break;
      case 'error':
        toastId = toast.error(message, toastOptions);
        break;
      case 'warning':
        toastId = toast.warning(message, toastOptions);
        break;
      case 'info':
      default:
        toastId = toast.info(message, toastOptions);
        break;
    }

    // Play audio notification simultaneously
    try {
      await audioNotificationService.playAudioForNotification(type, message);
    } catch (error) {
      console.error('Error playing audio for toast:', error);
      // Don't fail the toast if audio fails
    }

    return toastId;
  }, []);

  // Specific toast functions with audio
  const success = useCallback((message, options = {}) => {
    return showToast(message, 'success', options);
  }, [showToast]);

  const error = useCallback((message, options = {}) => {
    return showToast(message, 'error', options);
  }, [showToast]);

  const warning = useCallback((message, options = {}) => {
    return showToast(message, 'warning', options);
  }, [showToast]);

  const info = useCallback((message, options = {}) => {
    return showToast(message, 'info', options);
  }, [showToast]);

  // New job specific notifications
  const newJob = useCallback(async (message, jobSource = 'general', options = {}) => {
    const toastId = toast.info(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options
    });

    try {
      await audioNotificationService.playNewJobAudio(jobSource);
    } catch (error) {
      console.error('Error playing new job audio:', error);
    }

    return toastId;
  }, []);

  // Job accepted notifications
  const jobAccepted = useCallback(async (message, jobSource = 'general', options = {}) => {
    const toastId = toast.success(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options
    });

    try {
      await audioNotificationService.playJobAcceptedAudio(jobSource);
    } catch (error) {
      console.error('Error playing job accepted audio:', error);
    }

    return toastId;
  }, []);

  // Test audio notifications
  const testAudio = useCallback(async (type = 'info') => {
    const message = `Testing ${type} audio notification`;
    const toastId = showToast(message, type);
    await audioNotificationService.testAudio(type);
    return toastId;
  }, [showToast]);

  return {
    // Enhanced toast functions
    success,
    error,
    warning,
    info,
    showToast,
    
    // Job-specific functions
    newJob,
    jobAccepted,
    
    // Utility functions
    testAudio,
    
    // Audio service controls
    audioService: audioNotificationService
  };
};

export default useAudioToast;
